import {
  AutomatedNotificationType,
  AutomationTemplateKey,
  ConditionLogicConfigurationCollection,
  ConditionLogicId,
  IAutomatedNotificationConfiguration,
  IAutomationTiming,
  IBrand,
  ITemplateDefinition,
  SystemTemplate,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  IReffable,
  WithRef,
  doc,
  multiFilter,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { ConditionLogic } from '../../condition-logic/condition-logic';
import { type IAppointmentDepositIsUnpaidConditionConfig } from '../../condition-logic/deposit-conditions';
import { AutomationConfiguration } from './automation-configuration';
import { SystemTemplateAdoption } from '../system-template-adoption';

/** Describes a reusable automation that can be instantiated into a brand. */
export interface IAutomationTemplate {
  key: AutomationTemplateKey;
  name: string;
  description: string;
  /** Which system template document to use for the notification content. */
  systemTemplate: SystemTemplate;
  type: AutomatedNotificationType;
  subject: string;
  timing: IAutomationTiming;
  requiredConditions?: ConditionLogicConfigurationCollection;
}

const depositReminderEmail: IAutomationTemplate = {
  key: AutomationTemplateKey.DepositReminderEmail,
  name: 'Deposit Reminder Email',
  description:
    'Sends an automated email reminder to patients with an unpaid deposit 48 hours before their appointment.',
  systemTemplate: SystemTemplate.DepositReminderEmail,
  type: AutomatedNotificationType.EMAIL,
  subject: 'Reminder: Deposit Required',
  timing: {
    amount: 48,
    unit: TimingUnit.Hours,
    direction: TimingDirection.Before,
  },
  requiredConditions: ConditionLogic.collection([
    ConditionLogic.and([
      ConditionLogic.custom<IAppointmentDepositIsUnpaidConditionConfig>(
        ConditionLogicId.AppointmentDepositIsUnpaid,
        { isUnpaid: true }
      ),
    ]),
  ]),
};

/**
 * Manages pre-defined automations that can be instantiated into a brand.
 * Deduplication is via `automationTemplate` key on the stored configuration —
 * a stable provenance value that survives template renames and soft-deletes.
 */
export class AutomationTemplate {
  /**
   * Returns all registered automation templates available for instantiation
   * into a brand.
   */
  static getTemplates(): IAutomationTemplate[] {
    return [depositReminderEmail];
  }

  /**
   * Returns the existing automation configurations whose `automationTemplate`
   * key matches the given template. Key-based matching survives template
   * renames and soft-deletes.
   */
  static getExistingConfigurations$(
    template: IAutomationTemplate,
    brand: IReffable<IBrand>
  ): Observable<WithRef<IAutomatedNotificationConfiguration>[]> {
    return AutomationConfiguration.getNotificationConfigurations$(brand).pipe(
      multiFilter((config) => config.automationTemplate === template.key)
    );
  }

  /**
   * Idempotently seeds the template for the given brand. Ensures the
   * system template document exists (creating it from the repository default
   * when absent, or clearing a soft-delete flag when it was previously
   * removed), then returns early if a configuration with a matching
   * `automationTemplate` key already exists. Otherwise creates the automation
   * configuration and patches `automatedConfigurationRef` back onto the
   * template document. Safe to call multiple times.
   */
  static async seed(
    template: IAutomationTemplate,
    brand: WithRef<IBrand>
  ): Promise<void> {
    const templateRef = SystemTemplateAdoption.docRef(
      template.systemTemplate,
      brand
    );
    const configCollection =
      AutomationConfiguration.col<IAutomatedNotificationConfiguration>(brand);
    const configRef = doc(configCollection, template.key);

    await Firestore.runTransaction(async (transaction) => {
      const existingTemplate = await Firestore.safeGetDoc(
        templateRef,
        transaction
      );
      const existingConfiguration = await Firestore.safeGetDoc(
        configRef,
        transaction
      );

      if (existingConfiguration && !existingConfiguration.deleted) {
        return;
      }

      await SystemTemplateAdoption.write(
        template.systemTemplate,
        brand,
        existingTemplate,
        transaction
      );
      await Firestore.addDoc(
        configCollection,
        AutomationTemplate.buildConfiguration(template, templateRef),
        template.key,
        transaction
      );
      await Firestore.patchDoc(
        templateRef,
        { automatedConfigurationRef: configRef },
        transaction
      );
    });
  }

  /**
   * Builds the `IAutomatedNotificationConfiguration` data object from the
   * template definition and a resolved template document reference. Public so
   * that test fixtures build the same shape production writes, rather than
   * duplicating it and silently drifting.
   */
  static buildConfiguration(
    template: IAutomationTemplate,
    templateRef: DocumentReference<ITemplateDefinition>
  ): IAutomatedNotificationConfiguration {
    return {
      ...AutomationConfiguration.init({ isActive: true }),
      name: template.name,
      subject: template.subject,
      type: template.type,
      timing: template.timing,
      requiredConditions: template.requiredConditions,
      templateRef,
      automationTemplate: template.key,
    };
  }
}
